/**
 * The kitchen renderer: one canvas, one requestAnimationFrame loop.
 *
 * It reads `feed.snapshot()` and nothing else — no socket, no React state, no
 * props that carry game data. Identical for live and replay (resolved
 * question 5), which is why `/replay` can mount the same `Stage` the round page
 * does and get the same picture.
 *
 * The one non-feed input is the `Catalog`: static content from `/api/config`
 * describing which ingredients exist and how they are produced. It is not game
 * state — it does not change during a round, it is the same object on both
 * pages, and the renderer still imports nothing from `net/`.
 *
 * The canvas is responsive. It resizes its backing store to the
 * element and the device pixel ratio on any frame where they disagree, so a
 * 5x5 kitchen, a 10x15 kitchen and a 380-pixel-wide phone all lay out from the
 * same code.
 */

import type { Cell, SerialisedState, Station } from '@overcooked/shared'
import type { EventFeed, FeedSnapshot } from '../feed/feed'
import type { Catalog } from './catalog'
import { EMPTY_CATALOG } from './catalog'
import { drawFocus, drawPlayer, facingCell } from './players'
import type { CueCategory } from './reasons'
import { refusalFor } from './reasons'
import { drawStation } from './stations'
import { COLOURS, roundRect, seatIdentity } from './theme'

const TAU = Math.PI * 2

interface Renderer {
  stop(): void
}

/** Space the page has claimed on top of the canvas for HUD overlays. */
export interface Insets {
  top: number
  right: number
  bottom: number
  left: number
}

export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 }

interface RendererOptions {
  /** The self ring is drawn for this player. `null` on the replay page. */
  getSelfId?: () => string | null
  /** Static content. Falls back to generated icons before `/api/config` lands. */
  getCatalog?: () => Catalog
  /** CSS pixels the HUD occupies, so the kitchen never sits under a ticket. */
  getInsets?: () => Insets
  /**
   * Tiles to keep lit while everything else is dimmed — the tutorial's
   * spotlight, and nothing else uses it. `null` or an empty list means no dim
   * at all, which is what a round always passes.
   */
  getSpotlight?: () => readonly Cell[] | null
}

interface Bounds {
  width: number
  height: number
  tile: number
  offsetX: number
  offsetY: number
}

/**
 * Where the kitchen goes.
 *
 * The grid is fitted into whatever the HUD has left and centred in it, with a
 * minimum tile size so a 10x15 map on a narrow phone shrinks the *view* rather
 * than becoming unreadable — the canvas is scrollable in that case because the
 * element is, not because the renderer clips.
 */
export function gridBounds(
  cssWidth: number,
  cssHeight: number,
  state: SerialisedState,
  insets: Insets,
): Bounds {
  let width = 0
  let height = 0
  for (const s of state.stations) {
    width = Math.max(width, s.cell.x + 1)
    height = Math.max(height, s.cell.y + 1)
  }

  const availableW = Math.max(40, cssWidth - insets.left - insets.right)
  const availableH = Math.max(40, cssHeight - insets.top - insets.bottom)
  const tile = Math.max(
    16,
    Math.floor(Math.min(availableW / Math.max(width, 1), availableH / Math.max(height, 1))),
  )

  return {
    width,
    height,
    tile,
    offsetX: Math.round(insets.left + (availableW - tile * width) / 2),
    offsetY: Math.round(insets.top + (availableH - tile * height) / 2),
  }
}

/** Beyond this a tile stops gaining legibility and starts looking like a toy. */
export const MAX_TILE = 132

/**
 * How wide the stage wants to be: exactly the two rails plus the kitchen.
 *
 * Round 1 fitted the kitchen to the height it had left and then padded the
 * stage out to a minimum width so the ticket rail across the top had room. The
 * result was a 510 px grid sitting in an 885 px panel — a column of map with
 * unused margin either side, which is the single thing that made the frame read
 * as unfinished. Now the rails carry the tickets and the round, and the stage is
 * their width plus the map's: there is no leftover margin inside the panel at
 * all, and the space that used to be empty is doing a job.
 *
 * Pure, so the three presets and a phone can be asserted without a browser.
 */
export function stageWidth(
  dims: { w: number; h: number },
  elementHeight: number,
  insets: Insets,
  maxTile: number = MAX_TILE,
): number {
  if (dims.h <= 0 || dims.w <= 0) return 0
  const usable = Math.max(40, elementHeight - insets.top - insets.bottom)
  const tile = Math.min(maxTile, Math.max(16, Math.floor(usable / dims.h)))
  return Math.round(dims.w * tile + insets.left + insets.right)
}

/**
 * The room the kitchen sits in.
 *
 * The first pass left the map floating in a flat void, and on a wide monitor
 * that void was most of the frame — it read as unfinished rather than as
 * framing. So the space around the kitchen is now a *place*: a dark service
 * floor of board courses running the width of the canvas, a warm pool of light
 * over the kitchen from the pass above it, and a vignette pulling the corners
 * down. Nothing here carries meaning; its whole job is to stop the emptiness
 * looking like an omission.
 */
function drawRoom(ctx: CanvasRenderingContext2D, w: number, h: number, b: Bounds | null): void {
  ctx.fillStyle = COLOURS.ink
  ctx.fillRect(0, 0, w, h)

  // Board courses. Long, low-contrast, and offset per row so the floor has a
  // direction; at this contrast it is texture rather than pattern.
  const course = Math.max(18, (b?.tile ?? 48) * 0.62)
  ctx.save()
  ctx.strokeStyle = 'rgba(255, 226, 186, 0.035)'
  ctx.lineWidth = 1
  for (let y = course; y < h; y += course) {
    ctx.beginPath()
    ctx.moveTo(0, Math.round(y) + 0.5)
    ctx.lineTo(w, Math.round(y) + 0.5)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)'
  for (let row = 0; row * course < h; row += 1) {
    const y = row * course
    const stagger = (row % 3) * course * 1.7
    for (let x = stagger; x < w; x += course * 5.1) {
      ctx.beginPath()
      ctx.moveTo(Math.round(x) + 0.5, y)
      ctx.lineTo(Math.round(x) + 0.5, y + course)
      ctx.stroke()
    }
  }
  ctx.restore()

  const focusX = b ? b.offsetX + (b.tile * b.width) / 2 : w * 0.5
  const focusY = b ? b.offsetY + (b.tile * b.height) * 0.42 : h * 0.42

  // The light over the pass: warm, centred on the kitchen, and elliptical so a
  // portrait map on a landscape canvas is lit along its own axis.
  ctx.save()
  ctx.translate(focusX, focusY)
  ctx.scale(1, b ? Math.max(0.55, (b.height * b.tile) / Math.max(1, b.width * b.tile)) : 1)
  const reach = b ? Math.max(b.width * b.tile, 320) * 1.15 : Math.max(w, h) * 0.7
  const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, reach)
  glow.addColorStop(0, 'rgba(126, 88, 48, 0.5)')
  glow.addColorStop(0.55, 'rgba(78, 52, 28, 0.22)')
  glow.addColorStop(1, 'rgba(0, 0, 0, 0)')
  ctx.fillStyle = glow
  ctx.beginPath()
  ctx.arc(0, 0, reach, 0, TAU)
  ctx.fill()
  ctx.restore()

  // Vignette. Holds the eye in the middle third, which is where the kitchen is.
  const vignette = ctx.createRadialGradient(
    w * 0.5,
    h * 0.5,
    Math.min(w, h) * 0.3,
    w * 0.5,
    h * 0.5,
    Math.max(w, h) * 0.78,
  )
  vignette.addColorStop(0, 'rgba(0,0,0,0)')
  vignette.addColorStop(1, 'rgba(0,0,0,0.6)')
  ctx.fillStyle = vignette
  ctx.fillRect(0, 0, w, h)

  if (!b) return

  // The kitchen slab, lifted off the floor by a shadow and a lit top edge, so
  // the map is an object standing in the room rather than a hole cut in it.
  const gw = b.tile * b.width
  const gh = b.tile * b.height
  const radius = Math.max(4, b.tile * 0.14)
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.7)'
  ctx.shadowBlur = Math.max(18, b.tile * 0.7)
  ctx.shadowOffsetY = Math.max(6, b.tile * 0.22)
  ctx.fillStyle = COLOURS.wall
  roundRect(ctx, b.offsetX, b.offsetY, gw, gh, radius)
  ctx.fill()
  ctx.restore()

  ctx.save()
  roundRect(ctx, b.offsetX - 1.5, b.offsetY - 1.5, gw + 3, gh + 3, radius + 1.5)
  ctx.lineWidth = 3
  ctx.strokeStyle = 'rgba(255, 214, 160, 0.14)'
  ctx.stroke()
  ctx.restore()
}

function drawKitchen(
  ctx: CanvasRenderingContext2D,
  state: SerialisedState,
  b: Bounds,
  atTick: number,
  catalog: Catalog,
): void {
  ctx.save()
  roundRect(ctx, b.offsetX, b.offsetY, b.tile * b.width, b.tile * b.height, Math.max(4, b.tile * 0.18))
  ctx.clip()
  for (const station of state.stations) {
    drawStation(
      ctx,
      station,
      {
        x: b.offsetX + station.cell.x * b.tile,
        y: b.offsetY + station.cell.y * b.tile,
        size: b.tile,
      },
      atTick,
      catalog,
      sprayAngle(state, station),
      holderColour(state, station),
    )
  }
  ctx.restore()
}

/**
 * The seat colour of whoever has the key down at this station, or `null`.
 *
 * The same lookup the spray angle does, for the same reason and from the same
 * two facts: the station carries `heldBy`, the state carries the seat. It is
 * what lets a chef tell their own chop from their partner's without the
 * renderer having to know which seat is "mine" — both bars are attributed, so
 * the question "is anybody on that" is answered for either player.
 */
function holderColour(state: SerialisedState, station: Station): string | null {
  if (station.resumedAtTick === null || !station.heldBy) return null
  const holder = state.players.find((p) => p.id === station.heldBy)
  return holder ? seatIdentity(holder.seat).colour : null
}

/**
 * Which way the foam is travelling at a burning station, or `null`.
 *
 * The station knows *who* is holding the key (`heldBy`); only the state knows
 * where they are standing. The angle runs from the chef to the tile, so a chef
 * below a fire sprays upward and one to its left sprays right — the effect
 * belongs to the player who is doing the work, and pointing it at them would
 * read as the fire attacking them.
 */
function sprayAngle(state: SerialisedState, station: Station): number | null {
  if (!station.onFire || station.resumedAtTick === null || !station.heldBy) return null
  const holder = state.players.find((p) => p.id === station.heldBy)
  if (!holder) return null
  const dx = station.cell.x - holder.cell.x
  const dy = station.cell.y - holder.cell.y
  if (dx === 0 && dy === 0) return null
  return Math.atan2(dy, dx)
}

/**
 * The spotlight: everything dimmed except the tiles the lesson is about.
 *
 * Craft Together does this with an SVG mask over DOM panels, cutting a hole
 * around a measured bounding box. This kitchen is a canvas, so the same idea
 * lands one layer down — a fill over the whole surface with the focus tiles
 * punched out of it by `evenodd`, and the rects come from the layout maths
 * `gridBounds` already did rather than from `getBoundingClientRect`.
 *
 * Drawn after the stations and before the chefs, deliberately: a player dimmed
 * out of their own kitchen would be a tutorial that hid the one thing the keys
 * are moving. The dim mutes the room; the people stay lit.
 */
function drawSpotlight(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  b: Bounds,
  cells: readonly Cell[],
): void {
  const pad = b.tile * 0.08
  const radius = Math.max(4, b.tile * 0.16)
  const hole = (cell: Cell): void =>
    roundedSubPath(
      ctx,
      b.offsetX + cell.x * b.tile - pad,
      b.offsetY + cell.y * b.tile - pad,
      b.tile + pad * 2,
      b.tile + pad * 2,
      radius,
    )

  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, w, h)
  for (const cell of cells) hole(cell)
  ctx.fillStyle = 'rgba(6, 5, 4, 0.72)'
  ctx.fill('evenodd')
  ctx.restore()

  // A thin warm edge on each lit tile. The hole alone reads as a gap in the
  // dark; the edge makes it read as something being pointed at.
  ctx.save()
  ctx.strokeStyle = 'rgba(255, 205, 130, 0.5)'
  ctx.lineWidth = Math.max(1.5, b.tile * 0.035)
  ctx.beginPath()
  for (const cell of cells) hole(cell)
  ctx.stroke()
  ctx.restore()
}

/**
 * A rounded rectangle appended to the current path.
 *
 * `theme.ts`'s `roundRect` opens a path of its own, which is right everywhere
 * else and wrong here: the dim is one path of many subpaths filled `evenodd`,
 * so each hole has to join the path rather than replace it.
 */
function roundedSubPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2))
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

/**
 * The transient cues, none of which is a word or an emoji any more.
 *
 *   collision  a radiating starburst on the contested cell
 *   contested  the same starburst — it is the same idea, aimed at a station
 *   blocked    a shock ring where the player bounced off
 *   failed     a shape chosen by the *category* of the refusal
 *
 * All of them are drawn shapes rather than glyphs, because an emoji renders
 * differently on every platform and is invisible in a screenshot taken on a box
 * with no emoji font — which is how they looked in the phase-7 captures.
 *
 * A refusal used to be one red cross for all thirty-seven reasons, which told
 * the player "no" and never "why". The reason now goes through `reasons.ts`,
 * which buckets it into six categories, and the *shape* carries the bucket:
 *
 *   needs_something  a ring with a piece missing — something is not here
 *   hands_or_space   a bracketed square — no room, wrong place
 *   ruined           the cross, kept for what it always really meant
 *   on_fire          a hazard triangle
 *   not_yet          a clock wedge — right idea, wrong moment
 *
 * Six shapes rather than thirty-seven: the shape is glimpsed peripherally and
 * says what kind of problem this is, and the flash line says which one. Colour
 * only reinforces the shape, per the rule in `theme.ts`.
 */
function drawCues(
  ctx: CanvasRenderingContext2D,
  cues: FeedSnapshot['cues'],
  b: Bounds,
  now: number,
): void {
  for (const cue of cues) {
    const age = now - cue.shownAt
    const life = Math.max(0, 1 - age / 450)
    if (life <= 0) continue
    const cx = b.offsetX + cue.cell.x * b.tile + b.tile / 2
    const cy = b.offsetY + cue.cell.y * b.tile + b.tile / 2

    ctx.save()
    if (cue.kind === 'blocked') {
      ctx.globalAlpha = life * 0.7
      ctx.strokeStyle = COLOURS.paper
      ctx.lineWidth = Math.max(2, b.tile * 0.06)
      ctx.beginPath()
      ctx.arc(cx, cy, b.tile * (0.2 + (1 - life) * 0.28), 0, TAU)
      ctx.stroke()
    } else if (cue.kind === 'failed') {
      ctx.globalAlpha = life * 0.85
      ctx.lineWidth = Math.max(3, b.tile * 0.1)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      drawRefusal(ctx, refusalFor(cue.reason).category, cx, cy, b.tile)
    } else {
      // The 💥, drawn: a twelve-pointed burst that expands as it fades.
      const grow = 1 + (1 - life) * 0.6
      ctx.globalAlpha = life
      ctx.fillStyle = COLOURS.brass
      ctx.beginPath()
      for (let i = 0; i < 12; i += 1) {
        const angle = (i / 12) * TAU
        const radius = b.tile * (i % 2 === 0 ? 0.44 : 0.2) * grow
        const px = cx + Math.cos(angle) * radius
        const py = cy + Math.sin(angle) * radius
        if (i === 0) ctx.moveTo(px, py)
        else ctx.lineTo(px, py)
      }
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = COLOURS.ember
      ctx.beginPath()
      ctx.arc(cx, cy, b.tile * 0.14 * grow, 0, TAU)
      ctx.fill()
    }
    ctx.restore()
  }
}

/**
 * One refusal, as a shape.
 *
 * Stroke geometry only — the caller has already set alpha, width and caps, so
 * every category fades on the same curve and reads at the same weight. Colour
 * is chosen here but never carries the meaning by itself: blood for a refusal,
 * ember for the fire, ice for "wait", exactly the jobs `theme.ts` gives them.
 */
function drawRefusal(
  ctx: CanvasRenderingContext2D,
  category: CueCategory,
  cx: number,
  cy: number,
  tile: number,
): void {
  const r = tile * 0.26

  if (category === 'needs_something') {
    // A ring with a bite out of it: the shape of a thing that is not here.
    ctx.strokeStyle = COLOURS.blood
    ctx.beginPath()
    ctx.arc(cx, cy, r, TAU * 0.08, TAU * 0.92)
    ctx.stroke()
    return
  }

  if (category === 'hands_or_space') {
    // Two brackets closing on nothing: no room here.
    ctx.strokeStyle = COLOURS.blood
    const arm = r * 0.62
    for (const side of [-1, 1]) {
      const x = cx + side * r
      ctx.beginPath()
      ctx.moveTo(x - side * arm, cy - r)
      ctx.lineTo(x, cy - r)
      ctx.lineTo(x, cy + r)
      ctx.lineTo(x - side * arm, cy + r)
      ctx.stroke()
    }
    return
  }

  if (category === 'on_fire') {
    // A hazard triangle, the one mark on the tile that means "this first".
    ctx.strokeStyle = COLOURS.ember
    ctx.beginPath()
    ctx.moveTo(cx, cy - r)
    ctx.lineTo(cx + r * 0.95, cy + r * 0.7)
    ctx.lineTo(cx - r * 0.95, cy + r * 0.7)
    ctx.closePath()
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(cx, cy - r * 0.3)
    ctx.lineTo(cx, cy + r * 0.22)
    ctx.stroke()
    return
  }

  if (category === 'not_yet') {
    // A clock with a wedge cut from it: the answer is yes, later.
    ctx.strokeStyle = COLOURS.ice
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, TAU)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(cx, cy - r * 0.72)
    ctx.lineTo(cx, cy)
    ctx.lineTo(cx + r * 0.6, cy + r * 0.34)
    ctx.stroke()
    return
  }

  // `ruined`, `contested`, and anything a future server invents: the cross this
  // file has always drawn, now meaning the one thing it was always best at —
  // that is spoiled, throw it away.
  ctx.strokeStyle = COLOURS.blood
  ctx.beginPath()
  ctx.moveTo(cx - r, cy - r)
  ctx.lineTo(cx + r, cy + r)
  ctx.moveTo(cx + r, cy - r)
  ctx.lineTo(cx - r, cy + r)
  ctx.stroke()
}

/**
 * How long the pointer to the extinguisher rack stays up.
 *
 * A cue lives 450 ms, which is right for a mark that only has to say "not
 * there" and wrong for a mark that is giving directions across a kitchen. This
 * matches `REFUSAL_LIFETIME_MS`, so the sentence in the flash line and the line
 * drawn on the floor arrive and leave together and read as one message.
 */
const FIRE_POINTER_MS = 2000

/**
 * Where the extinguisher actually is.
 *
 * Somebody's hands first. A fire no longer racks the extinguisher when it goes
 * out, so "in a chef's hands" is now the ordinary place for it to be between
 * one fire and the next — and pointing at a rack it is not on is worse than
 * useless. The carrier wins and the pointer becomes "ask them", which is the
 * true answer. Then a stocked rack, then wherever it has been set down, then
 * any rack at all, because a rack is where it will be put back.
 */
function extinguisherAt(state: SerialisedState): Cell | null {
  const carrier = state.players.find((p) => p.holding?.type === 'equipment')
  if (carrier) return carrier.cell
  const racks = state.stations.filter((s) => s.type === 'extinguisher_rack')
  const stocked = racks.find((s) => s.item?.type === 'equipment')
  if (stocked) return stocked.cell
  const parked = state.stations.find((s) => s.item?.type === 'equipment')
  if (parked) return parked.cell
  return racks[0]?.cell ?? null
}

/**
 * The refusal at a fire, answered with a direction rather than a sentence.
 *
 * `station_on_fire` is the one refusal whose fix is somewhere else on the map,
 * and a line of text at the bottom of the screen is the worst possible way to
 * say "go there" to somebody who is already panicking. So the tile draws a
 * pulsing dashed run from the fire to the extinguisher, ending in a ring around
 * it. The dashes crawl toward the target: motion carries the direction even if
 * the arrowhead is missed.
 */
function drawFirePointer(
  ctx: CanvasRenderingContext2D,
  state: SerialisedState,
  from: Cell,
  b: Bounds,
  age: number,
  now: number,
): void {
  const to = extinguisherAt(state)
  if (!to) return
  if (to.x === from.x && to.y === from.y) return

  const life = Math.max(0, 1 - age / FIRE_POINTER_MS)
  if (life <= 0) return

  const centre = (cell: Cell): [number, number] => [
    b.offsetX + cell.x * b.tile + b.tile / 2,
    b.offsetY + cell.y * b.tile + b.tile / 2,
  ]
  const [x0, y0] = centre(from)
  const [x1, y1] = centre(to)
  const angle = Math.atan2(y1 - y0, x1 - x0)
  // Start and end clear of both tiles, so the line joins them rather than
  // sitting on top of the flames at one end and the rack at the other.
  const gap = b.tile * 0.45
  const sx = x0 + Math.cos(angle) * gap
  const sy = y0 + Math.sin(angle) * gap
  const ex = x1 - Math.cos(angle) * gap
  const ey = y1 - Math.sin(angle) * gap

  // Fade in over the first fifth and out over the last, and breathe throughout.
  const pulse = 0.62 + 0.38 * Math.abs(Math.sin(now * 0.006))

  ctx.save()
  ctx.globalAlpha = Math.min(1, life * 1.6) * pulse
  ctx.lineWidth = Math.max(2, b.tile * 0.075)
  ctx.lineCap = 'round'
  ctx.strokeStyle = COLOURS.paper
  const dash = b.tile * 0.22
  ctx.setLineDash([dash, dash * 0.7])
  ctx.lineDashOffset = -((now * 0.06) % (dash * 1.7))
  ctx.beginPath()
  ctx.moveTo(sx, sy)
  ctx.lineTo(ex, ey)
  ctx.stroke()
  ctx.setLineDash([])

  // The head, at the extinguisher end.
  const head = b.tile * 0.24
  ctx.beginPath()
  ctx.moveTo(ex, ey)
  ctx.lineTo(ex - Math.cos(angle - 0.42) * head, ey - Math.sin(angle - 0.42) * head)
  ctx.moveTo(ex, ey)
  ctx.lineTo(ex - Math.cos(angle + 0.42) * head, ey - Math.sin(angle + 0.42) * head)
  ctx.stroke()

  // A ring round the thing you are being sent to fetch, in the fire's own
  // colour so the two ends of the message are visibly one message.
  ctx.strokeStyle = COLOURS.ember
  ctx.lineWidth = Math.max(2, b.tile * 0.06)
  ctx.beginPath()
  ctx.arc(x1, y1, b.tile * (0.4 + 0.06 * pulse), 0, TAU)
  ctx.stroke()
  ctx.restore()
}

/** Match the backing store to the element. Returns the CSS-pixel size. */
function fit(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): [number, number] {
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const cssW = canvas.clientWidth || canvas.width
  const cssH = canvas.clientHeight || canvas.height
  const wantW = Math.round(cssW * dpr)
  const wantH = Math.round(cssH * dpr)
  if (canvas.width !== wantW || canvas.height !== wantH) {
    canvas.width = wantW
    canvas.height = wantH
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return [cssW, cssH]
}

export function startRenderer(
  canvas: HTMLCanvasElement,
  feed: EventFeed,
  options: RendererOptions = {},
): Renderer {
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context unavailable')

  const getSelfId = options.getSelfId ?? ((): string | null => null)
  const getCatalog = options.getCatalog ?? ((): Catalog => EMPTY_CATALOG)
  const getInsets = options.getInsets ?? ((): Insets => NO_INSETS)
  const getSpotlight = options.getSpotlight ?? ((): readonly Cell[] | null => null)

  let raf = 0

  /**
   * The last "it is on fire and you are not holding the extinguisher" refusal.
   *
   * Held here rather than read off the cue every frame because the cue expires
   * in 450 ms and the pointer needs `FIRE_POINTER_MS`. It is the only piece of
   * state this loop keeps, and it is decoration: nothing reads it back.
   */
  let firePointer: { cell: Cell; shownAt: number } | null = null

  const frame = (): void => {
    const { state, cues, tick } = feed.snapshot()
    const [w, h] = fit(canvas, ctx)

    const bounds = state ? gridBounds(w, h, state, getInsets()) : null
    drawRoom(ctx, w, h, bounds)

    if (state && bounds) {
      const catalog = getCatalog()
      const self = getSelfId()
      drawKitchen(ctx, state, bounds, tick, catalog)

      const spotlight = getSpotlight()
      if (spotlight && spotlight.length > 0) drawSpotlight(ctx, w, h, bounds, spotlight)

      // Facing, as a statement about a tile. Drawn after the stations so it
      // sits on the fixture it names, and before the bodies so a chef is never
      // hidden by their own marker. Clipped to the grid: a chef on the edge of
      // the map faces the wall, not the room.
      ctx.save()
      roundRect(
        ctx,
        bounds.offsetX,
        bounds.offsetY,
        bounds.tile * bounds.width,
        bounds.tile * bounds.height,
        Math.max(4, bounds.tile * 0.14),
      )
      ctx.clip()
      for (const player of state.players) {
        if (!player.connected) continue
        const target = facingCell(player.cell, player.facing)
        drawFocus(
          ctx,
          seatIdentity(player.seat),
          bounds.offsetX + target.x * bounds.tile,
          bounds.offsetY + target.y * bounds.tile,
          bounds.tile,
          player.facing,
        )
      }
      ctx.restore()

      for (const player of state.players) {
        drawPlayer(
          ctx,
          player,
          {
            cx: bounds.offsetX + player.cell.x * bounds.tile + bounds.tile / 2,
            cy: bounds.offsetY + player.cell.y * bounds.tile + bounds.tile / 2,
            tile: bounds.tile,
          },
          player.id === self,
          catalog,
          tick,
        )
      }
      const now = performance.now()

      // The pointer is drawn over the chefs, not under them: it is an
      // instruction to a person, not a thing in the kitchen.
      const fireRefusal = cues.find((c) => c.kind === 'failed' && c.reason === 'station_on_fire')
      if (fireRefusal) firePointer = { cell: fireRefusal.cell, shownAt: fireRefusal.shownAt }
      if (firePointer) {
        const age = now - firePointer.shownAt
        if (age >= FIRE_POINTER_MS) firePointer = null
        else drawFirePointer(ctx, state, firePointer.cell, bounds, age, now)
      }

      drawCues(ctx, cues, bounds, now)
    }

    raf = requestAnimationFrame(frame)
  }

  raf = requestAnimationFrame(frame)
  return { stop: () => cancelAnimationFrame(raf) }
}
